import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Users, Calendar, Flame, Store, MessageCircle, DollarSign, TrendingUp, Plus, Building2, ArrowRight } from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, ResponsiveContainer, Tooltip } from "recharts";
import api, { fileUrl } from "@/lib/api";
import { Loader, SectionHeader, EmptyState, PrimaryButton, money } from "@/components/ui-bits";
import { EventCard, ChallengeCard, ListingCard } from "@/components/cards";

const STATS = [
  { key: "members", label: "Members", icon: Users, c: "#55708F" },
  { key: "revenue", label: "Revenue (demo)", icon: DollarSign, c: "#FF6B00", money: true },
  { key: "events", label: "Events", icon: Calendar, c: "#8FAF9B" },
  { key: "challenges", label: "Challenges", icon: Flame, c: "#FF6B00" },
  { key: "listings", label: "Listings", icon: Store, c: "#55708F" },
  { key: "posts", label: "Posts", icon: MessageCircle, c: "#8FAF9B" },
];

export default function BuilderDashboard() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/builder/dashboard").then((r) => setD(r.data)).catch(() => setD({ communities: [] })); }, []);
  if (!d) return <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8"><Loader /></div>;

  if (!d.communities?.length) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-10">
        <SectionHeader eyebrow="Builder dashboard" title="Become a community builder" />
        <EmptyState icon={Building2} title="You haven't built a community yet"
          hint="Create your first community to unlock your builder dashboard — track members, host events, run challenges and monetize your passion."
          action={<PrimaryButton as={Link} to="/create/community" data-testid="dash-create-community">Create a community</PrimaryButton>} />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-10 space-y-10">
      <SectionHeader eyebrow="Builder dashboard" title="Grow your passion economy"
        subtitle="Everything you're building, in one place."
        action={<PrimaryButton as={Link} to="/create/community" data-testid="dash-new-community"><Plus className="h-4 w-4" />New community</PrimaryButton>} />

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
        {STATS.map((s) => (
          <div key={s.key} className="pa-card p-5" data-testid={`dash-stat-${s.key}`}>
            <span className="h-9 w-9 rounded-xl grid place-items-center" style={{ background: `${s.c}1f` }}><s.icon className="h-4.5 w-4.5" style={{ color: s.c }} /></span>
            <p className="text-2xl font-bold text-[#071126] mt-3">{s.money ? money(d.stats[s.key]) : d.stats[s.key]}</p>
            <p className="text-xs text-[#55708F] font-medium">{s.label}</p>
          </div>
        ))}
      </div>

      <div className="pa-card p-6">
        <div className="flex items-center gap-2 mb-4"><TrendingUp className="h-5 w-5 text-[#FF6B00]" /><h3 className="font-semibold text-[#071126]">Member growth</h3><span className="text-xs text-[#55708F] ml-2">demo trend</span></div>
        <div className="h-56">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={d.growth} margin={{ left: -20, right: 8, top: 8 }}>
              <defs><linearGradient id="g" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#FF6B00" stopOpacity={0.3} /><stop offset="100%" stopColor="#FF6B00" stopOpacity={0} /></linearGradient></defs>
              <XAxis dataKey="label" tick={{ fontSize: 12, fill: "#55708F" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 12, fill: "#55708F" }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid rgba(7,17,38,0.1)" }} />
              <Area type="monotone" dataKey="members" stroke="#FF6B00" strokeWidth={2.5} fill="url(#g)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      <section>
        <h3 className="text-lg font-semibold text-[#071126] mb-4">Your communities</h3>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {d.communities.map((c) => (
            <Link key={c.id} to={`/communities/${c.id}`} className="pa-card pa-card-hover p-4 flex items-center gap-3" data-testid={`dash-community-${c.id}`}>
              <img src={fileUrl(c.cover)} alt="" className="h-14 w-14 rounded-xl object-cover" />
              <div className="min-w-0 flex-1"><p className="font-semibold text-[#071126] truncate">{c.name}</p><p className="text-xs text-[#55708F] inline-flex items-center gap-1"><Users className="h-3.5 w-3.5" />{c.member_count} members</p></div>
              <ArrowRight className="h-4 w-4 text-[#55708F]" />
            </Link>
          ))}
        </div>
      </section>

      <div className="grid lg:grid-cols-2 gap-8">
        <section>
          <div className="flex items-center justify-between mb-4"><h3 className="text-lg font-semibold text-[#071126]">Your events</h3><PrimaryButton as={Link} to="/create/event" className="!px-3 !py-1.5 text-xs"><Plus className="h-3.5 w-3.5" />New</PrimaryButton></div>
          {d.events?.length ? <div className="grid gap-4">{d.events.map((e) => <EventCard key={e.id} e={e} />)}</div> : <EmptyState icon={Calendar} title="No events yet" hint="Host your first event." />}
        </section>
        <section>
          <div className="flex items-center justify-between mb-4"><h3 className="text-lg font-semibold text-[#071126]">Your challenges</h3><PrimaryButton as={Link} to="/create/challenge" className="!px-3 !py-1.5 text-xs"><Plus className="h-3.5 w-3.5" />New</PrimaryButton></div>
          {d.challenges?.length ? <div className="grid gap-4">{d.challenges.map((c) => <ChallengeCard key={c.id} c={c} />)}</div> : <EmptyState icon={Flame} title="No challenges yet" hint="Launch a challenge." />}
        </section>
      </div>

      <section>
        <div className="flex items-center justify-between mb-4"><h3 className="text-lg font-semibold text-[#071126]">Your marketplace listings</h3><PrimaryButton as={Link} to="/create/listing" className="!px-3 !py-1.5 text-xs"><Plus className="h-3.5 w-3.5" />New</PrimaryButton></div>
        {d.listings?.length ? <div className="grid gap-5 grid-cols-2 lg:grid-cols-4">{d.listings.map((l) => <ListingCard key={l.id} l={l} />)}</div> : <EmptyState icon={Store} title="Nothing listed yet" hint="Sell a creation or service to start monetizing." />}
      </section>
    </div>
  );
}
